import config from 'devextreme/core/config';
import dxButton from 'devextreme/ui/button';
import dxDropDownButton from 'devextreme/ui/drop_down_button';
import dxToolbar from 'devextreme/ui/toolbar';
import dxPopup from 'devextreme/ui/popup';
import dxScrollView from 'devextreme/ui/scroll_view';
import dxList, { ItemRenderedEvent as ListItemRenderedEvent } from 'devextreme/ui/list';
import dxTreeView, { ItemRenderedEvent as TreeViewItemRenderedEvent } from 'devextreme/ui/tree_view';
import { ILabelHeaderFilter } from '@interfaces/nebula';
import { licenseKey } from '../../devextreme-license';

/**
 * Setzt die DevExtreme-Lizenz und die Grundeinstellungen der Widgets.
 * Wird per `provideAppInitializer` vor dem ersten Rendern aufgerufen.
 */
export function applyDevExtremeDefaults(): void {
  config({ licenseKey });

  dxButton.defaultOptions({ options: { stylingMode: 'outlined' } });
  dxDropDownButton.defaultOptions({ options: { stylingMode: 'text' } });
  dxToolbar.defaultOptions({ options: { useFlatButtons: false } });
  dxPopup.defaultOptions({ options: { useFlatToolbarButtons: false } });
  dxScrollView.defaultOptions({ options: { useNative: false } });

  /* Tooltip für Listeneinträge (auch in SelectBox, TagBox, Header-Filter usw.) anhand des displayExpr */
  dxList.defaultOptions({
    device: { deviceType: 'desktop' },
    options: {
      onItemRendered: (args: ListItemRenderedEvent) =>
        setItemTitle(args.itemElement, getItemTitle(args.itemData, args.component.option('displayExpr')))
    }
  });
  dxTreeView.defaultOptions({
    device: { deviceType: 'desktop' },
    options: {
      onItemRendered: (args: TreeViewItemRenderedEvent) =>
        setItemTitle(args.itemElement, getItemTitle(args.itemData, args.component.option('displayExpr')))
    }
  });
}

/**
 * Ermittelt den Tooltip eines Listeneintrags. Der Text kommt aus `userName` (Header-Filter mit Benutzern),
 * sonst aus dem `displayExpr` des Widgets, sonst aus `text` bzw. dem primitiven Eintrag selbst.
 * Ist der Text weder Zeichenkette noch Zahl, wird `null` geliefert.
 */
export function getItemTitle(
  itemData: unknown,
  displayExpr: string | ((item: unknown) => unknown) | undefined
): string | null {
  let text: unknown;
  if (itemData !== null && typeof itemData === 'object') {
    const item = itemData as Partial<ILabelHeaderFilter>;
    if (item.userName) {
      text = item.userName;
    } else if (typeof displayExpr === 'function') {
      text = displayExpr(itemData);
    } else if (typeof displayExpr === 'string') {
      text = displayExpr
        .split('.')
        .reduce<unknown>((value, key) => (value as Record<string, unknown> | null | undefined)?.[key], itemData);
    } else {
      text = item.text;
    }
  } else {
    text = itemData;
  }
  return typeof text === 'string' || typeof text === 'number' ? String(text) : null;
}

/** Setzt das `title`-Attribut nur, wenn ein Text ermittelt wurde */
function setItemTitle(itemElement: HTMLElement, title: string | null): void {
  if (title !== null) {
    itemElement.setAttribute('title', title);
  }
}
